import { isoAttr, formatDateTime } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import type { components } from "@oathera/contract/api";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import {
  coreStateLevel,
  coreStateText,
  instanceLevel,
  instanceStateText,
} from "./vocab.ts";

/**
 * Instances and restarts (task 11.5; design 12.2 "instances and restarts, version and profile,
 * heartbeat detail"; 12.4). One row per reported instance of the service, through the semantic
 * `DenseTable`:
 *
 * - liveness (`state`/`stale`) as an icon+text status — a silent or stale instance is grey
 *   `unknown`, never green (design 12.3);
 * - version and profile (the "version and profile" the design calls for), with an honest em dash
 *   when the instance did not report one;
 * - environment;
 * - uptime, derived from `uptime_s`;
 * - the heartbeat detail: `last_heartbeat_at` as an absolute time, and `started_at`;
 * - Protected_Core state as an icon+text status (verified green, failed/absent error, other/none
 *   grey);
 * - restarts in the window (`restarts_in_range`), an exact count.
 *
 * Every value comes straight from the contract `Instance`; nothing is invented, and a missing field
 * reads as "n/a" rather than a fabricated value.
 */

type Instance = components["schemas"]["Instance"];

export interface InstancesTableProps {
  readonly instances: readonly Instance[];
}

export function InstancesTable(props: InstancesTableProps): ReactNode {
  // Show the most-restarted, least-live instances first so trouble surfaces at the top: silent/stale
  // before active, then by restart count descending.
  const rows = [...props.instances].sort((a, b) => {
    const liveRank = livenessRank(a) - livenessRank(b);
    if (liveRank !== 0) return liveRank;
    return b.restarts_in_range - a.restarts_in_range;
  });

  const columns: readonly Column<Instance>[] = [
    {
      key: "instance",
      header: "Instance",
      isRowHeader: true,
      cell: (i) => (
        <span className="svc-inst__id" title={`source ${i.source_id}`}>
          <code>{i.instance_id}</code>
        </span>
      ),
    },
    {
      key: "liveness",
      header: "Liveness",
      cell: (i) => (
        <StatusBadge
          level={instanceLevel(i)}
          label={instanceStateText(i)}
          size="sm"
          title={
            i.state === "silent"
              ? "No recent heartbeat (silent)."
              : i.stale
                ? "Data for this instance is stale."
                : "Active and reporting."
          }
        />
      ),
    },
    {
      key: "version",
      header: "Version",
      cell: (i) => (i.version === null ? emdash() : <code>{i.version}</code>),
    },
    {
      key: "profile",
      header: "Profile",
      cell: (i) => (i.profile === null ? emdash() : i.profile),
    },
    {
      key: "environment",
      header: "Environment",
      cell: (i) => (i.environment.length === 0 ? emdash() : i.environment),
    },
    {
      key: "core_state",
      header: "Core state",
      cell: (i) =>
        i.core_state === null ? (
          <StatusBadge level="unknown" label="No data" size="sm" />
        ) : (
          <StatusBadge
            level={coreStateLevel(i.core_state)}
            label={coreStateText(i.core_state)}
            size="sm"
            title={`Protected_Core: ${coreStateText(i.core_state).toLowerCase()}`}
          />
        ),
    },
    {
      key: "uptime",
      header: "Uptime",
      align: "right",
      cell: (i) => (i.uptime_s === null ? emdash() : formatDuration(i.uptime_s)),
    },
    {
      key: "heartbeat",
      header: "Last heartbeat",
      align: "right",
      cell: (i) => <Timestamp ms={i.last_heartbeat_at} />,
    },
    {
      key: "started",
      header: "Started",
      align: "right",
      cell: (i) => <Timestamp ms={i.started_at} />,
    },
    {
      key: "restarts",
      header: "Restarts",
      align: "right",
      // An exact count over the window.
      measure: "exact",
      cell: (i) => (
        <span className="svc-num" data-emphasis={i.restarts_in_range > 0 ? "true" : undefined}>
          {i.restarts_in_range.toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <section className="svc-instances" aria-label="Instances and restarts">
      <h2 className="svc-section__title">Instances and restarts</h2>
      <DenseTable
        caption="One row per reported instance, with version, profile, heartbeat, Protected_Core state and restarts."
        columns={columns}
        rows={rows}
        rowKey={(i) => `${i.source_id}:${i.instance_id}`}
        stickyHeader
        emptyMessage="No instances of this service have reported in the current window."
      />
    </section>
  );
}

/** Order for the troublesome-first sort: silent (0) < stale (1) < active (2). */
function livenessRank(i: Instance): number {
  if (i.state === "silent") return 0;
  if (i.stale) return 1;
  return 2;
}

/** An absolute timestamp with a machine-readable `dateTime`, or an em dash when there is none. */
function Timestamp(props: { ms: number | null }): ReactNode {
  if (props.ms === null) return emdash();
  const iso = isoAttr(props.ms);
  return (
    <time dateTime={iso} title={iso}>
      {formatDateTime(props.ms)}
    </time>
  );
}

/** A short, human duration from seconds (e.g. "3d 4h", "12m"). */
function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "n/a";
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function emdash(): ReactNode {
  return <span className="svc-muted">n/a</span>;
}
